import { Box, Checkbox, FormControlLabel, Grid } from "@mui/material";
import * as React from "react";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import { EventChartOptions, IEventChartOptions } from "../../../models/event/eventChartOptions";
import { IDashboardChartData } from "../../../models/report/dashboardChartData";
import * as eventService from "../../../models/services/eventService";
import { FilterButtons } from "../../framework/Filters/FilterButtons";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { DashboardBarChart } from "../../framework/Chart/DashboardBarChart";
import { GridRow } from "../../framework/Grid/GridRow";
import { PageDrawer } from "../../framework/Page/PageDrawer";
import { AppSettingsIcon } from "source/components/framework/Icon/AppSettingsIcon";
import { frontSetEvents } from "source/models/store/front";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";

//EventChartPropertiesDialog
export interface IEventChartPropertiesDialogProp {
    classes?: string;
    panelSize: number;
    panelVisible: boolean;
    onSavePanelSize: (size: number, visible: boolean) => void;
    onClose: () => void;
}

export interface IEventChartPropertiesDialogState {
    //Initial values
    initialized: boolean;
    eventTypes: IIdTitleNode[];
    groupingTypes: IIdTitle[];
    timeSpans: IIdTitle[];
    //Modified values
    panelSize: string;
    panelVisible: boolean;
    eventTypeIds: string[];
    groupingType: string;
    timeSpan: string;
}

export class EventChartPropertiesDialog extends React.Component<IEventChartPropertiesDialogProp, IEventChartPropertiesDialogState> {
    private orgStateHash: string = "";

    constructor(props: IEventChartPropertiesDialogProp) {
        super(props);
        this.state = {
            initialized: false,
            panelSize: props.panelSize.toString(10),
            panelVisible: props.panelVisible,
            eventTypes: [],
            groupingTypes: [],
            timeSpans: [],
            eventTypeIds: [],
            groupingType: "",
            timeSpan: ""
        };
    }

    getDialogData = async () => {
        const result = await AppUtils.callService(() => eventService.getEventChartPropertiesDialogData(), false);
        if (!result) return;
        const chartOptions = result.chartOptions;
        const newState = {
            ...this.state,
            initialized: true,
            eventTypes: result.eventTypes,
            groupingTypes: result.groupingTypes,
            timeSpans: result.timeSpans,
            eventTypeIds: chartOptions.selectedEventTypeIds.slice(0),
            groupingType: chartOptions.selectedGroupingType,
            timeSpan: chartOptions.selectedTimeSpan
        };
        this.setState(newState);
        const saveData = this.getSaveDataFromState(this.props, newState);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.getDialogData();
    }

    handleEventTypesChange = (eventTypeIds: string[]) => {
        this.setState({ eventTypeIds });
    };

    changePanelVisibility = (panelVisible: boolean) => {
        this.setState({ panelVisible });
    };

    getSaveDataFromState = (props: IEventChartPropertiesDialogProp, state: IEventChartPropertiesDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("eventTypeIds", state.eventTypeIds);
        result.append("timeSpan", state.timeSpan.toInteger());
        result.append("groupingType", state.groupingType.toInteger());
        return result;
    };

    saveToDb = async () => {
        const props = this.props;
        const state = this.state;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => eventService.saveEventChartOptions(saveData.obj), false);
        if (!result) return;
        //Set app state
        const chartOptions = new EventChartOptions();
        chartOptions.selectedEventTypeIds = state.eventTypeIds;
        chartOptions.selectedGroupingType = state.groupingType;
        chartOptions.selectedTimeSpan = state.timeSpan;
        store.customStore.dispatch(frontSetEvents({ chartOptions }));
        //Close dialog
        props.onClose();
        //Save panel size
        props.onSavePanelSize(state.panelSize.toInteger(), state.panelVisible);
    };

    handleSave = () => {
        this.saveToDb();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.saveToDb();
        } else {
            props.onClose();
        }
    };

    render() {
        const state = this.state;
        if (!state.initialized) return null;
        return (
            <PageDrawer
                open
                icon={<AppSettingsIcon />}
                title={Translations.Events}
                parentTitle={Translations.Panels}
                onClose={this.handleCancel}
            >
                <Box display="flex" flexDirection="column" flexGrow={2} margin="24px">
                    <Box display="flex" flexDirection="column" flexGrow={2}>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormControlLabel
                                    label={Translations.PanelVisible}
                                    control={
                                        <Checkbox
                                            checked={state.panelVisible}
                                            onChange={(_e, checked) => this.changePanelVisibility(checked)}
                                        />
                                    }
                                />
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.PanelSize}>
                                    <IdTitleDropdown
                                        selectedId={state.panelSize}
                                        items={[
                                            { id: "1", title: Translations.PanelSize1 },
                                            { id: "2", title: Translations.PanelSize2 },
                                            { id: "5", title: Translations.PanelSize5 },
                                            { id: "3", title: Translations.PanelSize3 },
                                            { id: "4", title: Translations.PanelSize4 },
                                            { id: "6", title: Translations.PanelSize6 }
                                        ]}
                                        onChange={(panelSize) => this.setState({ panelSize })}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Interval}>
                                    <IdTitleDropdown
                                        selectedId={state.timeSpan}
                                        items={state.timeSpans.map((i) => {
                                            return { id: i.id, title: i.title };
                                        })}
                                        onChange={(timeSpan) => this.setState({ timeSpan })}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.ResultGrouping}>
                                    <IdTitleDropdown
                                        selectedId={state.groupingType}
                                        items={state.groupingTypes.map((i) => {
                                            return { id: i.id, title: i.title };
                                        })}
                                        onChange={(groupingType) => this.setState({ groupingType })}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.EventTypes}>
                                    <TreeCheckboxDropdown
                                        required={false}
                                        selectedIds={state.eventTypeIds}
                                        values={state.eventTypes}
                                        onChange={this.handleEventTypesChange}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </Box>
                    <FilterButtons
                        chartPanelPropertiesMode
                        onCancel={this.handleCancel}
                        onSave={this.handleSave}
                    />
                </Box>
            </PageDrawer>
        );
    }
}

// EventChart
export interface IEventChartProp {
    classes?: string;
    panelPosition: number;
    panelSize: number;
    panelVisible: boolean;
    companyId?: string;
    disableGlobalLoading?: boolean;
    chartData?: IDashboardChartData;
    chartOptions?: IEventChartOptions;
    preloaded?: boolean;
    showEditButton?: boolean;
    showSearchFilter?: boolean;
    showPropertiesButton?: boolean;
    onSavePanelSize: (size: number, visible: boolean) => void;
}

interface IEventChartState {
    intialized: boolean;
    isLoading: boolean;
    waitingPreloadedData: boolean;
    panelSize: number;
    chartData: IDashboardChartData;
    showChartOptionsDialog: boolean;
}

export class EventChart extends React.Component<IEventChartProp, IEventChartState> {
    _isMounted: boolean;

    constructor(props: IEventChartProp) {
        super(props);
        this.state = {
            intialized: !!props.chartData,
            isLoading: false,
            waitingPreloadedData: props.chartData ? false : (props.preloaded ?? false),
            panelSize: props.panelSize,
            chartData: props.chartData,
            showChartOptionsDialog: false
        };
    }

    getChartData = async (companyId: string) => {
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => eventService.getChartData(companyId), false, this.props.disableGlobalLoading, () => {
            if (!this._isMounted) return;
            this.setState({
                isLoading: false
            });
        });
        if (!result || !this._isMounted) return;
        this.setState({
            intialized: true,
            chartData: result
        });
    };

    componentDidMount(): void {
        const props = this.props;
        if (props.preloaded) return;
        this.getChartData(props.companyId);
        this._isMounted = true;
    }

    componentDidUpdate(prevProps: IEventChartProp, prevState: IEventChartState) {
        const props = this.props;
        const state = this.state;
        if (state.waitingPreloadedData) {
            if (props.chartData || !state.chartData) {
                this.setState({
                    intialized: true,
                    waitingPreloadedData: false,
                    chartData: props.chartData,
                });
            }
            return;
        }
        const prevChartOptionsHash = prevProps.chartOptions ? JSON.stringify(prevProps.chartOptions) : "";
        const chartOptionsHash = props.chartOptions ? JSON.stringify(props.chartOptions) : "";
        const prevChartDataHash = prevProps.chartData ? JSON.stringify(prevProps.chartData) : "";
        const chartDataHash = props.chartData ? JSON.stringify(props.chartData) : "";
        if (prevProps.panelSize === props.panelSize && prevProps.panelPosition === props.panelPosition && prevChartDataHash === chartDataHash && prevChartOptionsHash === chartOptionsHash) return;
        if (prevChartOptionsHash !== chartOptionsHash) {
            this.getChartData(props.companyId);
            return;
        }
        this.setState({
            intialized: true,
            panelSize: props.panelSize
        });
    }

    componentWillUnmount(): void {
        this._isMounted = false;
    }

    handlePropertiesClick = () => {
        this.setState({
            showChartOptionsDialog: true
        });
    };

    handleChartOptionsDialogClose = () => {
        this.setState({
            showChartOptionsDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <DashboardBarChart
                classes={props.classes}
                defaultTitle={Translations.Events}
                titlePrefix={Translations.Events + ": "}
                intialized={state.intialized}
                isLoading={state.isLoading}
                chartData={state.chartData}
                onPropertiesClick={props.showPropertiesButton ? this.handlePropertiesClick : null}
            >
                {state.showChartOptionsDialog &&
                    <EventChartPropertiesDialog
                        panelSize={state.panelSize}
                        panelVisible={props.panelVisible}
                        onSavePanelSize={props.onSavePanelSize}
                        onClose={this.handleChartOptionsDialogClose}
                    />
                }
            </DashboardBarChart>
        );
    }
}